import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useCallback } from 'react'
import useSWRMutation from 'swr/mutation'

import { filterPositiveCourseThresholds } from './courseThresholds'

const API_V1_PREFIX = '/api/v1'
const FFS_LTA_GENERATION_PATH = `${API_V1_PREFIX}/lta-generation/ffs`

export interface ComputeLocalThinAreasResponse {
  cdf_id: string
  indication_set: string
  lta_count: number
  entry_count: number
  entry_ids: string[]
  sandbox: boolean
}

// Request body for the newer `/lta-generation/ffs` endpoint. The client can
// send course thresholds, API 510, observation-publishing, circumferential-axis,
// and optional expansion-distance/unit settings. Other endpoint knobs
// (t_upper_threshold, max_ruler_distance, alpha, sandbox) retain server defaults
// and are omitted. `/ffs` always generates flaws, CTPs, and rulers.
export interface GenerateFfsLtaStructuresRequest {
  cds_id: string
  df_settings_key: string
  t_threshold: number
  thickness_units: string
  indication_set: string
  courses?: Record<string, number>
  enable_api510?: boolean
  circumferential_axis?: 'x' | 'y'
  publish_observations?: boolean
  expansion_distance?: number
  distance_units?: string
}

export function buildFfsLtaGenerationRequest({
  cdsId,
  dfSettingsKey,
  tThreshold,
  thicknessUnits,
  indicationSet,
  courseThresholds,
  enableApi510 = true,
  publishObservations = false,
  circumferentialAxis = 'x',
  expansionDistance,
  distanceUnits,
}: {
  cdsId: string
  dfSettingsKey: string
  tThreshold: number
  thicknessUnits: string
  indicationSet: string
  courseThresholds?: Record<string, number | undefined | null>
  enableApi510?: boolean
  publishObservations?: boolean
  circumferentialAxis?: 'x' | 'y'
  expansionDistance?: number
  distanceUnits?: string
}): GenerateFfsLtaStructuresRequest {
  const request: GenerateFfsLtaStructuresRequest = {
    cds_id: cdsId,
    df_settings_key: dfSettingsKey,
    t_threshold: tThreshold,
    thickness_units: thicknessUnits,
    indication_set: indicationSet,
    enable_api510: enableApi510,
    circumferential_axis: circumferentialAxis,
    publish_observations: publishObservations,
  }

  const filteredCourseThresholds = filterPositiveCourseThresholds(
    courseThresholds,
    (threshold) => Number(threshold)
  )

  if (Object.keys(filteredCourseThresholds).length > 0) {
    request.courses = filteredCourseThresholds
  }

  if (expansionDistance !== undefined) {
    request.expansion_distance = expansionDistance
  }

  if (distanceUnits) {
    request.distance_units = distanceUnits
  }

  return request
}

function useGenerateFfsLtaStructuresEndpoint() {
  const { FFS_SERVICE_URL } = useRuntimeVariables()

  if (!FFS_SERVICE_URL) {
    return null
  }

  return `${FFS_SERVICE_URL.replace(/\/$/, '')}${FFS_LTA_GENERATION_PATH}`
}

// Active client for the newer `/lta-generation/ffs` endpoint.
export function useGenerateFfsLtaStructures() {
  const { authorizedAxios } = useGeckoAuth()
  const endpoint = useGenerateFfsLtaStructuresEndpoint()

  const { error, isMutating, trigger } = useSWRMutation<
    ComputeLocalThinAreasResponse,
    Error,
    string | null,
    GenerateFfsLtaStructuresRequest
  >(
    endpoint,
    async (url: string, { arg }: { arg: GenerateFfsLtaStructuresRequest }) => {
      const response =
        await authorizedAxios.post<ComputeLocalThinAreasResponse>(url, arg, {
          headers: { 'Content-Type': 'application/json' },
        })

      return response.data
    }
  )

  const generateFfsLtaStructures = useCallback(
    async (request: GenerateFfsLtaStructuresRequest) => {
      if (!endpoint) {
        throw new Error('FFS service URL is not configured.')
      }

      return trigger(request)
    },
    [endpoint, trigger]
  )

  return {
    generateFfsLtaStructures,
    isConfigured: endpoint !== null,
    isGeneratingFfsLtaStructures: isMutating,
    error,
  }
}
